import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';

import { BannerCategoryBlock as BannerCategoryBlockV3 } from '~/components/isolated/CategoryBlocks/BannerCategoryBlock';
import { BannerMainBlock as BannerMainBlockV3 } from '~/components/isolated/CategoryBlocks/BannerMainBlock';
import { BannerPromoBlock as BannerPromoBlockV3 } from '~/components/isolated/CategoryBlocks/BannerPromoBlock';
import { CategoriesCarouselSliderOneBlock as CategoriesCarouselSliderOneBlockV3 } from '~/components/isolated/CategoryBlocks/CategoriesCarouselSliderOneBlock';
import { CategoriesGrid1Brand } from '~/components/isolated/CategoryBlocks/CategoriesGrid1Brand';
import { CategoriesGrid2Brand } from '~/components/isolated/CategoryBlocks/CategoriesGrid2Brand';
import { CategoriesGrid2M } from '~/components/isolated/CategoryBlocks/CategoriesGrid2M';
import { CategoriesPromo } from '~/components/isolated/CategoryBlocks/CategoriesPromo';
import { CategoriesTilesThreeLevelBlock as CategoriesTilesThreeLevelBlockV3 } from '~/components/isolated/CategoryBlocks/CategoriesTilesThreeLevelBlock';
import { CategoriesTilesTwoLevelBlock as CategoriesTilesTwoLevelBlockV3 } from '~/components/isolated/CategoryBlocks/CategoriesTilesTwoLevelBlock';
import { CategoriesTilesXSScrollable as CategoriesTilesXSScrollableV3 } from '~/components/isolated/CategoryBlocks/CategoriesTilesXSScrollable';
import { ListOfCategories1 } from '~/components/isolated/CategoryBlocks/ListOfCategories1';
import { ProductsCarouselSliderAllHits } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderAllHits';
import { ProductsCarouselSliderOneBlock as ProductsCarouselSliderOneBlockV3 } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderOneBlock';
import { ProductsCarouselSliderSuperPrices } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderSuperPrices';
import { ProductsCarouselSliderWithBackground } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderWithBackground';
import { ProductsGridMultirow } from '~/components/isolated/CategoryBlocks/ProductsGridMultirow';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import BannerLinkBlock from './BannerLinkBlock/BannerLinkBlock';
import BannerProductBlock from './BannerProductBlock/BannerProductBlock';
import CategoriesCarouselCircleBlock from './CategoriesCarouselCircleBlock/CategoriesCarouselCircleBlock';
import CategoriesCarouselSliderThreeWBlock from './CategoriesCarouselSliderThreeWBlock/CategoriesCarouselSliderThreeWBlock';
import categoriesCarouselSliderThreeStyles from './CategoriesCarouselSliderThreeWBlock/CategoriesCarouselSliderThreeWBlock.module.scss';
import CategoriesCarouselSliderTwoSBlock from './CategoriesCarouselSliderTwoSBlock/CategoriesCarouselSliderTwoSBlock';
import CategoriesCarouselSquareThreeWBlock from './CategoriesCarouselSquareThreeWBlock/CategoriesCarouselSquareThreeWBlock';
import CategoriesGridOneSBlockFactory from './CategoriesGridOneSBlock/CategoriesGridOneSBlockFactory';
import CategoriesGridThreeMBlock from './CategoriesGridThreeMBlock/CategoriesGridThreeMBlock';
import { CategoriesTilesXSMultirow } from './CategoriesTilesXSMultirow';
import CategoryBlockErrorBoundary from './CategoryBlockErrorBoundary';
import ListOfCategoriesDE from './ListOfCategoriesDE/ListOfCategoriesDE';
import ListOfCategoriesTwoBlock from './ListOfCategoriesTwoBlock/ListOfCategoriesTwoBlock';
import ProductsGridBlock from './ProductsGridBlock/ProductsGridBlock';

export enum CategoryDisplayType {
  MAIN_BANNER = 'main_banner',
  BANNER_CATEGORY = 'banner_category',
  PRODUCTS_CAROUSEL_SLIDER_ONE = 'products_carousel_slider_1',
  PRODUCTS_CAROUSEL_SLIDER_ALL_HITS = 'products_carousel_slider_all_hits',
  PRODUCTS_CAROUSEL_SLIDER_SUPER_PRICES = 'products_carousel_slider_super_prices',
  PRODUCTS_GRID_MULTIROW = 'products_grid_multirow',
  PRODUCTS_CAROUSEL_SLIDER_WITH_BACKGROUND = 'products_carousel_slider_with_background',
  BANNER_PRODUCT = 'banner_product',
  BANNER_LINK = 'banner_link',
  BANNER_PROMO = 'promo_banner',
  PRODUCTS_GRID = 'products_grid',
  LIST_OF_CATEGORIES_ONE = 'list_of_categories_1',
  LIST_OF_CATEGORIES_TWO = 'list_of_categories_2',
  CATEGORIES_CAROUSEL_SQUARE_ONE = 'categories_carousel_square',
  CATEGORIES_CAROUSEL_CIRCLE = 'categories_carousel_circle',
  CATEGORIES_CAROUSEL_SLIDER_ONE = 'categories_carousel_slider_1',
  CATEGORIES_CAROUSEL_SLIDER_TWO = 'categories_carousel_slider_2',
  CATEGORIES_CAROUSEL_SLIDER_THREE = 'categories_carousel_slider_3',
  CATEGORIES_FILES_SX_SCROLLABLE = 'categories_tiles_xs_scrollable',
  CATEGORIES_FILES_SX_MULTIROW = 'categories_tiles_xs_multirow',
  CATEGORIES_FILES_SX_SCROLLABLE_LVL_3 = 'categories_tiles_xs_scrollable_3_lvl',
  CATEGORIES_FILES_SX_MULTIROW_LVL_3 = 'categories_tiles_xs_multirow_3_lvl',
  CATEGORIES_GRID_ONE = 'categories_grid_1',
  CATEGORIES_GRID_ONE_UNMASKED = 'categories_grid_1_unmasked',
  CATEGORIES_GRID_ONE_BRAND = 'categories_grid_1_brand',
  CATEGORIES_GRID_TWO = 'categories_grid_2',
  CATEGORIES_GRID_TWO_BRAND = 'categories_grid_2_brand',
  CATEGORIES_PROMO = 'categories_promo',
  CATEGORIES_TILES_TWO_LEVEL = 'categories_tiles_2_lvl',
  CATEGORIES_TILES_THREE_LEVEL = 'categories_tiles_3_lvl',
  LIST_OF_CATEGORIES_HOME = 'list_of_categories_home',

  // TODO: Deprecated 👇🏻. Remove after database migration
  CATEGORIES_GRID_THREE = 'categories_grid_3_level',
}

interface UnknownCategoryProps {
  name: string;
  displayType: Nullable<string>;
}

const UnknownCategory = ({ name, displayType }: UnknownCategoryProps) => {
  return import.meta.env.DEV ? (
    <div>
      Here will be a block with name: {name} and displayType: {displayType}
    </div>
  ) : null;
};

const categoryToBanners = (data: Category[]) => {
  return data.map(({ name, previewImage, previewImageThumb, id }) => ({
    src: previewImage || '',
    scrThumb: previewImageThumb || '',
    alt: name,
    linkId: id,
  }));
};

const renderCategory = ({
  category,
  isMobile,
  externalLink,
  legacy,
  priority,
}: {
  category: Category;
  isMobile: boolean;
  externalLink?: string;
  legacy?: boolean;
  priority?: boolean;
}) => {
  switch (category.displayType) {
    case CategoryDisplayType.MAIN_BANNER: {
      return (
        <BannerMainBlockV3
          banners={categoryToBanners(category.subcategory)}
          isMobile={isMobile}
          priority={priority}
        />
      );
    }
    case CategoryDisplayType.BANNER_CATEGORY: {
      return (
        <BannerCategoryBlockV3
          banners={categoryToBanners(category.subcategory)}
          isMobile={isMobile}
        />
      );
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_ONE: {
      return <ProductsCarouselSliderOneBlockV3 category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_ALL_HITS: {
      return (
        <ProductsCarouselSliderAllHits
          category={category}
          priority={priority}
        />
      );
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_SUPER_PRICES: {
      return <ProductsCarouselSliderSuperPrices category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_GRID_MULTIROW: {
      return <ProductsGridMultirow category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_CAROUSEL_SLIDER_WITH_BACKGROUND: {
      return (
        <ProductsCarouselSliderWithBackground
          category={category}
          priority={priority}
        />
      );
    }
    case CategoryDisplayType.BANNER_PRODUCT: {
      return <BannerProductBlock category={category} />;
    }
    case CategoryDisplayType.BANNER_LINK: {
      return <BannerLinkBlock category={category} />;
    }
    case CategoryDisplayType.BANNER_PROMO: {
      return <BannerPromoBlockV3 category={category} />;
    }
    case CategoryDisplayType.PRODUCTS_GRID: {
      return <ProductsGridBlock category={category} />;
    }
    case CategoryDisplayType.LIST_OF_CATEGORIES_ONE: {
      return <ListOfCategories1 category={category} priority={priority} />;
    }
    case CategoryDisplayType.LIST_OF_CATEGORIES_TWO: {
      return <ListOfCategoriesTwoBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_ONE: {
      return <CategoriesCarouselSliderOneBlockV3 category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_THREE: {
      return <CategoriesCarouselSliderThreeWBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SQUARE_ONE: {
      return <CategoriesCarouselSquareThreeWBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_CIRCLE: {
      return <CategoriesCarouselCircleBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_GRID_ONE: {
      return (
        <CategoriesGridOneSBlockFactory
          category={category}
          masked={true}
          externalLink={externalLink}
          legacy={legacy}
          priority={priority}
        />
      );
    }
    case CategoryDisplayType.CATEGORIES_GRID_ONE_UNMASKED: {
      return (
        <CategoriesGridOneSBlockFactory
          category={category}
          masked={false}
          externalLink={externalLink}
          legacy={legacy}
          priority={priority}
        />
      );
    }

    case CategoryDisplayType.CATEGORIES_GRID_ONE_BRAND: {
      return <CategoriesGrid1Brand category={category} priority={priority} />;
    }

    case CategoryDisplayType.CATEGORIES_GRID_TWO: {
      return <CategoriesGrid2M category={category} priority={priority} />;
    }
    case CategoryDisplayType.CATEGORIES_GRID_TWO_BRAND: {
      return <CategoriesGrid2Brand category={category} priority={priority} />;
    }
    // TODO: Deprecated 👇🏻. Remove after database migration
    case CategoryDisplayType.CATEGORIES_GRID_THREE:
    case CategoryDisplayType.CATEGORIES_TILES_TWO_LEVEL: {
      // `legacy` overrides the design system on purpose — Cerise and Heimkaup
      // ask for the grid block from /shop even though both are V3.
      const CategoriesTiles2Lvl = legacy
        ? CategoriesGridThreeMBlock
        : CategoriesTilesTwoLevelBlockV3;
      return <CategoriesTiles2Lvl category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_TILES_THREE_LEVEL: {
      return <CategoriesTilesThreeLevelBlockV3 category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_TWO: {
      return <CategoriesCarouselSliderTwoSBlock category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_SCROLLABLE: {
      return <CategoriesTilesXSScrollableV3 category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_SCROLLABLE_LVL_3: {
      return <CategoriesTilesXSScrollableV3 category={category} level={3} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_MULTIROW: {
      return <CategoriesTilesXSMultirow category={category} />;
    }
    case CategoryDisplayType.CATEGORIES_FILES_SX_MULTIROW_LVL_3: {
      return <CategoriesTilesXSMultirow category={category} level={3} />;
    }
    case CategoryDisplayType.CATEGORIES_PROMO: {
      return <CategoriesPromo category={category} priority={priority} />;
    }
    case CategoryDisplayType.LIST_OF_CATEGORIES_HOME: {
      return <ListOfCategoriesDE category={category} />;
    }
    default:
      return (
        <UnknownCategory
          name={category.name}
          displayType={category.displayType}
        />
      );
  }
};

interface CategoryBlockProps {
  category: Category;
  externalLink?: string;
  className?: string;
  /** Force the legacy (non-V3) block design even on V3 companies */
  legacy?: boolean;
  /** When true, prioritizes the first block's LCP image (e.g. top MAIN_BANNER when hero is absent) */
  priority?: boolean;
}

const CategoryBlock = ({
  category,
  externalLink,
  className,
  legacy,
  priority,
}: CategoryBlockProps) => {
  const { isMobile } = useGlobal();

  if (!category.subcategory.length) {
    return null;
  }

  return (
    <div
      className={classes([
        category.displayType ===
          CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_TWO &&
          categoriesCarouselSliderThreeStyles.rootWrapper,
        className,
      ])}
      data-display-type={category.displayType}
    >
      <CategoryBlockErrorBoundary parent={category?.displayType ?? 'unknown'}>
        {renderCategory({
          category,
          isMobile,
          externalLink,
          legacy,
          priority,
        })}
      </CategoryBlockErrorBoundary>
    </div>
  );
};

export default observer(CategoryBlock);
